// httpPorts — the Learning BC slice ("who this is for"). Create/update compose
// the Language primitives INSIDE the port, so the UI never calls a Language
// port directly.

import { Result } from "@bilda/utils";
import { mkMalformed } from "../externalError.js";
import type { LearningPorts } from "../ports/learning.js";
import type { PortResult } from "../ports/result.js";
import type {
    CreatedLearningContext,
    LearningContextSummary,
    NewLearningContext,
} from "../ports.js";
import { fields, list, str } from "../wireShape.js";
import { command } from "./shared.js";

// `POST /commands/listLearningContexts` → the audience picker. The workflow
// answers `{ items: [{ id, title, ...counts }] }`; the picker needs only id +
// title. An empty list is a valid `ok` (no audience framed yet).

const loadLearningContexts = async (): PortResult<
    readonly LearningContextSummary[]
> => {
    const r = await command("listLearningContexts", {});
    if (Result.isErr(r)) return r;
    const items = list(fields(r.value)?.get("items")).flatMap(
        (raw): readonly LearningContextSummary[] => {
            const f = fields(raw);
            const id = str(f?.get("id"));
            if (id === undefined) return [];
            return [{ id, title: str(f?.get("title")) ?? id }];
        },
    );
    return Result.ok(items);
};

// `POST /commands/showLearningContextFacets` → the full-facet read behind the
// SituationProfile panel. The workflow resolves the context + its bound
// instruction language profile into a flat DTO
// `{ title, variety, level, terminology, tone, instructionLanguageProfileId }`;
// this maps it to the panel's `NewLearningContext` (dropping the profile id,
// which `updateLearningContext` re-reads when it needs to write the facets).

// The raw facet read: the panel-facing facets PLUS the bound profile id the
// facet-write path (`updateLearningContext`) needs to target.
type ContextFacetsRead = {
    readonly instructionLanguageProfileId: string;
    readonly facets: NewLearningContext;
};

const readContextFacets = async (id: string): PortResult<ContextFacetsRead> => {
    const r = await command("showLearningContextFacets", { id });
    if (Result.isErr(r)) return r;
    const f = fields(r.value);
    const title = str(f?.get("title"));
    const variety = str(f?.get("variety"));
    const level = str(f?.get("level"));
    const terminology = str(f?.get("terminology"));
    const tone = str(f?.get("tone"));
    const instructionLanguageProfileId = str(
        f?.get("instructionLanguageProfileId"),
    );
    if (
        title === undefined ||
        variety === undefined ||
        level === undefined ||
        terminology === undefined ||
        tone === undefined ||
        instructionLanguageProfileId === undefined
    ) {
        return Result.err(
            mkMalformed(
                "showLearningContextFacets returned an incomplete shape",
            ),
        );
    }
    return Result.ok({
        instructionLanguageProfileId,
        facets: { title, language: variety, level, terminology, tone },
    });
};

const loadLearningContext = async (
    id: string,
): PortResult<NewLearningContext> => {
    const r = await readContextFacets(id);
    return Result.isErr(r) ? r : Result.ok(r.value.facets);
};

// One authoring gesture, one command: `createLearningContext` makes the
// context and its language profile (the facets given here, bound to both
// language sets) in one transaction. A refusal is a typed `ExternalError` the
// dialog surfaces.

const createLearningContext = async (
    input: NewLearningContext,
): PortResult<CreatedLearningContext> => {
    const rCtx = await command("createLearningContext", {
        title: input.title,
        language: input.language,
        level: input.level,
        terminology: input.terminology,
        tone: input.tone,
    });
    if (Result.isErr(rCtx)) return rCtx;
    const learningContextId = str(fields(rCtx.value)?.get("learningContextId"));
    if (learningContextId === undefined) {
        return Result.err(
            mkMalformed("createLearningContext returned no learningContextId"),
        );
    }
    return Result.ok({ learningContextId });
};

// Edit an EXISTING audience in place. The facets span two aggregates, so the
// update mirrors the create split: the register facets
// (variety/level/terminology/tone) go to the bound language profile via
// `updateLanguageProfile`; the title goes to the context via
// `renameLearningContext`. First re-read the facets to resolve the bound
// profile id (the panel carries only the facet values, not the profile id),
// then write the profile, then the title. A refusal at any step is a typed
// `ExternalError` the panel surfaces.
//
// MVP shortcut (flagged): the two writes are not atomic — a failure between
// them can leave facets updated but title not (or vice versa). Both commands
// are idempotent-retryable, matching create's own non-atomic two-command
// gesture; a durable multi-command PM is post-MVP.
const updateLearningContext = async (
    id: string,
    input: NewLearningContext,
): PortResult<CreatedLearningContext> => {
    const rRead = await readContextFacets(id);
    if (Result.isErr(rRead)) return rRead;
    const rProfile = await command("updateLanguageProfile", {
        languageProfileId: rRead.value.instructionLanguageProfileId,
        variety: input.language,
        level: input.level,
        terminology: input.terminology,
        tone: input.tone,
    });
    if (Result.isErr(rProfile)) return rProfile;
    const rRename = await command("renameLearningContext", {
        learningContextId: id,
        title: input.title,
    });
    if (Result.isErr(rRename)) return rRename;
    return Result.ok({ learningContextId: id });
};

export const learningHttp: LearningPorts = {
    loadLearningContexts,
    loadLearningContext,
    createLearningContext,
    updateLearningContext,
};
